import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  AddMonitoredServerRequest,
  MonitoredServer,
  UpdateMonitoredServerRequest,
} from "@xop/dashboard-shared";
import { useState, type FormEvent } from "react";
import type { JSX } from "react";

import { ApiError, api } from "../../../lib/api.js";
import { MONITORED_KEY } from "./monitoredKey.js";

/* ---------- Add modal ---------------------------------------------- */

export function AddServerModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const body: AddMonitoredServerRequest = {
        label: label.trim(),
        url: url.trim(),
      };
      return api<MonitoredServer>("/api/servers/monitored", {
        method: "POST",
        body,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MONITORED_KEY });
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not add server",
      );
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !url.trim()) {
      setError("Label and URL are required");
      return;
    }
    setError(null);
    add.mutate();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add a server</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit} sx={{ display: "grid", gap: 1.5, pt: 1 }}>
          <Field
            label="Label"
            value={label}
            onChange={setLabel}
            placeholder="conferweb-c1"
            autoFocus
          />
          <Field
            label="URL"
            value={url}
            onChange={setUrl}
            placeholder="https://c1.conferweb.com"
            type="url"
          />
          {error && <Typography sx={{ fontSize: 12, color: "error.main" }}>{error}</Typography>}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
            <Button variant="text" color="inherit" size="small" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disableElevation size="small" disabled={add.isPending}>
              {add.isPending ? "Adding…" : "Add server"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Edit modal --------------------------------------------- */

export function EditServerModal({
  server,
  onClose,
}: {
  server: MonitoredServer;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [label, setLabel] = useState(server.label);
  const [url, setUrl] = useState(server.url);
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: async () => {
      const trimmedLabel = label.trim();
      const trimmedUrl = url.trim();
      const patch: UpdateMonitoredServerRequest = {};
      if (trimmedLabel !== server.label) patch.label = trimmedLabel;
      if (trimmedUrl !== server.url) patch.url = trimmedUrl;
      if (Object.keys(patch).length === 0) return server;
      return api<MonitoredServer>(`/api/servers/monitored/${server.id}`, {
        method: "PATCH",
        body: patch,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MONITORED_KEY });
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not update server",
      );
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !url.trim()) {
      setError("Label and URL are required");
      return;
    }
    setError(null);
    update.mutate();
  };

  const dirty = label.trim() !== server.label || url.trim() !== server.url;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`Edit ${server.label}`}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit} sx={{ display: "grid", gap: 1.5, pt: 1 }}>
          <Field
            label="Label"
            value={label}
            onChange={setLabel}
            placeholder="conferweb-c1"
            autoFocus
          />
          <Field
            label="URL"
            value={url}
            onChange={setUrl}
            placeholder="https://c1.conferweb.com"
            type="url"
          />
          {error && <Typography sx={{ fontSize: 12, color: "error.main" }}>{error}</Typography>}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
            <Button variant="text" color="inherit" size="small" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disableElevation size="small" disabled={update.isPending || !dirty}>
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  autoFocus,
  placeholder,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
}): JSX.Element {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      {...(autoFocus ? { autoFocus: true } : {})}
      {...(placeholder ? { placeholder } : {})}
      {...(type ? { type } : {})}
    />
  );
}
